/**
 * features/onboarding/sub-verticals — Seed catalog of BusinessProfile keys
 * surfaced in the Step 2 sub-vertical picker.
 *
 * In production this list comes from `GET /api/business/onboarding/sub-verticals`
 * (proxied to BusinessProfileRegistry — ADR-100). During Faz 1.2 we ship a
 * seeded catalog so the wizard is usable before the backend endpoint exists.
 *
 * Shape and keys MUST match the production registry once it ships — this file
 * is the single source of truth for FE-only milestones.
 *
 * @see ADR-100 (BusinessProfileRegistry)
 * @see ADR-108 (Tenant Onboarding Automation) §1 Step 2
 */
import type { OperatingHoursEntry, SubVerticalOption } from './types';

/**
 * Build weekday hours using JS weekday indexing (0=Sun … 6=Sat). Omitted
 * weekdays are returned as closed. Matches `Date.prototype.getDay()` and the
 * Lovable primitive contract.
 */
function defaultHours(
  ranges: Partial<Record<0 | 1 | 2 | 3 | 4 | 5 | 6, [string, string]>>,
): OperatingHoursEntry[] {
  return ([0, 1, 2, 3, 4, 5, 6] as const).map(weekday => {
    const slot = ranges[weekday];
    return slot
      ? { weekday, openTime: slot[0], closeTime: slot[1] }
      : { weekday, openTime: null, closeTime: null };
  });
}

const MON_SAT_9_18 = defaultHours({
  1: ['09:00', '18:00'],
  2: ['09:00', '18:00'],
  3: ['09:00', '18:00'],
  4: ['09:00', '18:00'],
  5: ['09:00', '18:00'],
  6: ['09:00', '18:00'],
});

const WEEKDAY_8_19 = defaultHours({
  1: ['08:00', '19:00'],
  2: ['08:00', '19:00'],
  3: ['08:00', '19:00'],
  4: ['08:00', '19:00'],
  5: ['08:00', '19:00'],
});

const ALL_DAYS_CONTINUOUS = defaultHours({
  0: ['00:00', '23:59'],
  1: ['00:00', '23:59'],
  2: ['00:00', '23:59'],
  3: ['00:00', '23:59'],
  4: ['00:00', '23:59'],
  5: ['00:00', '23:59'],
  6: ['00:00', '23:59'],
});

export const SUB_VERTICAL_SEED: SubVerticalOption[] = [
  {
    key: 'grooming-salon',
    category: 'grooming',
    displayName: { tr: 'Pet Kuaför Salonu', en: 'Grooming Salon' },
    description: {
      tr: 'Yıkama, tüy kesimi, tırnak bakımı — salon bazlı hizmet.',
      en: 'Bath, haircut, nail trim — salon-based service.',
    },
    engineKind: 'slot',
    modules: ['grooming-checklist', 'before-after-photo', 'price-matrix'],
    defaultOperatingHours: MON_SAT_9_18,
  },
  {
    key: 'grooming-mobile',
    category: 'mobile-service',
    displayName: { tr: 'Mobil Pet Kuaför', en: 'Mobile Groomer' },
    description: {
      tr: 'Müşterinin adresine giden tek-araç kuaför hizmeti.',
      en: 'Single-vehicle mobile grooming at the customer’s address.',
    },
    engineKind: 'slot',
    modules: ['route-planner', 'grooming-checklist', 'mobile-inventory'],
    defaultOperatingHours: WEEKDAY_8_19,
    complianceHints: [
      { tr: 'Araç ruhsatı ve hijyen sertifikası', en: 'Vehicle license + hygiene certificate' },
    ],
  },
  {
    key: 'vet-clinic',
    category: 'veterinary',
    displayName: { tr: 'Veteriner Kliniği', en: 'Veterinary Clinic' },
    description: {
      tr: 'Fiziksel muayene, aşı, laboratuvar — poliklinik.',
      en: 'Physical exam, vaccination, lab — walk-in clinic.',
    },
    engineKind: 'consultation',
    modules: ['medical-record', 'prescription', 'lab-orders', 'vaccination-schedule'],
    defaultOperatingHours: WEEKDAY_8_19,
    complianceHints: [
      { tr: 'Veteriner hekim diploması ve muayenehane ruhsatı zorunludur', en: 'Veterinarian license + clinic permit required' },
    ],
  },
  {
    key: 'vet-telehealth',
    category: 'veterinary',
    displayName: { tr: 'Tele-Veteriner', en: 'Televet' },
    description: {
      tr: 'Online video konsültasyon ve elektronik reçete.',
      en: 'Online video consultation and e-prescription.',
    },
    engineKind: 'consultation',
    modules: ['video-session', 'prescription', 'medical-record'],
    defaultOperatingHours: WEEKDAY_8_19,
    complianceHints: [
      { tr: 'Veteriner hekim diploması zorunludur', en: 'Veterinarian license required' },
    ],
  },
  {
    key: 'boarding',
    category: 'boarding',
    displayName: { tr: 'Pet Pansiyonu', en: 'Pet Boarding' },
    description: {
      tr: 'Gecelik konaklama, kafesli veya villa tipi — 7 gün operasyon.',
      en: 'Overnight stays (kennels or villas) — 7-day operation.',
    },
    engineKind: 'nightly',
    modules: ['kennel-map', 'feed-schedule', 'pickup-dropoff', 'night-watch-log'],
    defaultOperatingHours: ALL_DAYS_CONTINUOUS,
  },
  {
    key: 'training-group',
    category: 'training',
    displayName: { tr: 'Eğitim Merkezi', en: 'Training Center' },
    description: {
      tr: 'Grup ve birebir eğitim — sabit program takibi.',
      en: 'Group and private training — fixed curriculum tracking.',
    },
    engineKind: 'event',
    modules: ['curriculum-tracker', 'progress-report', 'packages'],
    defaultOperatingHours: MON_SAT_9_18,
  },
  {
    key: 'walking-solo',
    category: 'walking',
    displayName: { tr: 'Köpek Gezdirici (Solo)', en: 'Solo Dog Walker' },
    description: {
      tr: 'Tek kişilik alan operasyonu — gezdirme rotası + GPS.',
      en: 'Single-operator field service — walk route + GPS.',
    },
    engineKind: 'slot',
    modules: ['route-tracker', 'gps-proof-of-service', 'recurring-bookings'],
    defaultOperatingHours: WEEKDAY_8_19,
  },
  {
    key: 'daycare',
    category: 'daycare',
    displayName: { tr: 'Günlük Bakım', en: 'Day Care' },
    description: {
      tr: 'Gündüz sosyalleşme + aktivite programı.',
      en: 'Daytime socialization and activity program.',
    },
    engineKind: 'slot',
    modules: ['playgroup-roster', 'activity-log', 'daily-report'],
    defaultOperatingHours: WEEKDAY_8_19,
  },
];

export function findSubVertical(key: string): SubVerticalOption | undefined {
  return SUB_VERTICAL_SEED.find(o => o.key === key);
}
